import React, { useCallback, useEffect, useState } from 'react';
import GenericForm, { GenericFormDef, GenericFormAction } from '../commons/GenericForm';

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface KanbanFormProps {
  /** ID of the actividad task */
  taskId: string | number;
  /** Called after a successful submit */
  onSuccess?: (values: Record<string, any>, action: GenericFormAction | null) => void;
  /** Called when the user cancels */
  onCancel?: () => void;
}

export default function KanbanForm({ taskId, onSuccess, onCancel }: KanbanFormProps) {
  const [formDef,      setFormDef]      = useState<GenericFormDef | null>(null);
  const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState(false);

  const fetchForm = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { ...authHeader() };
      const [defRes, valRes] = await Promise.all([
        fetch(`/api/usuario/me/actividad/${taskId}/form`,        { headers }),
        fetch(`/api/usuario/me/actividad/${taskId}/form/values`, { headers }),
      ]);
      if (!defRes.ok) throw new Error(`Error ${defRes.status} al cargar el formulario`);
      if (!valRes.ok) throw new Error(`Error ${valRes.status} al cargar los valores`);
      const [def, vals] = await Promise.all([defRes.json(), valRes.json()]);
      setFormDef(def);
      setInitialValues(vals ?? {});
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar el formulario');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  const handleSubmit = async (values: Record<string, any>, action: GenericFormAction | null) => {
    setSaving(true);
    setError(null);
    try {
      const { _files, ...plainValues } = values;
      const actionKey = action?.param ?? action?.label ?? 'submit';
      const url = `/api/usuario/me/actividad/${taskId}/form/action/${encodeURIComponent(actionKey)}`;

      // Always send multipart so the backend receives a consistent format.
      // The JSON fields go in a "data" part; each file gets its own part named after the field.
      const fd = new FormData();
      fd.append('data', JSON.stringify(plainValues));
      if (_files) {
        Object.entries(_files as Record<string, File>).forEach(([fieldName, file]) => {
          fd.append(fieldName, file, file.name);
        });
      }

      const res = await fetch(url, {
        method: 'POST',
        // No Content-Type header — browser sets multipart/form-data + boundary automatically
        headers: { ...authHeader() },
        body: fd,
      });
      if (!res.ok) {
        let msg = `Error ${res.status}`;
        try {
          const text = await res.text();
          if (text) {
            try {
              const body = JSON.parse(text);
              msg = body?.details ?? body?.detail ?? body?.message ?? body?.title ?? body?.error ?? text;
            } catch {
              msg = text; // plain-text error body
            }
          }
        } catch { /* ignore read errors */ }
        throw new Error(msg);
      }
      setSuccess(true);
      onSuccess?.(values, action);
    } catch (e: any) {
      setError(e?.message ?? 'Error al enviar el formulario');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={s.center}>
        <div style={s.spinner} />
        <span style={{ fontSize: 13, color: '#80868b' }}>Cargando formulario…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.errorBox}>
        <span>{error}</span>
        <button style={s.retryBtn} onClick={fetchForm}>Reintentar</button>
      </div>
    );
  }

  if (success) {
    return (
      <div style={s.successBox}>
        ✅ Formulario enviado correctamente.
      </div>
    );
  }

  if (!formDef || !initialValues) return null;

  return (
    <div style={s.root}>
      <div style={s.sectionTitle}>Formulario de actividad</div>
      <GenericForm
        formDef={formDef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={() => onCancel?.()}
        saving={saving}
        accentColor="#3b82f6"
      />
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  root:        { marginTop: 16 },
  sectionTitle:{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 },
  center:      { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0', color: '#80868b', fontSize: 13 },
  spinner:     { width: 18, height: 18, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#3b82f6', animation: 'spin 0.7s linear infinite', flexShrink: 0 },
  errorBox:    { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13 },
  retryBtn:    { all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#3b82f6', textDecoration: 'underline' },
  successBox:  { padding: '10px 14px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13 },
};
